import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBell, 
  FaToggleOn, 
  FaToggleOff, 
  FaVolumeUp, 
  FaVolumeMute,
  FaExchangeAlt,
  FaShieldAlt,
  FaCog,
  FaRocket,
  FaUsers,
  FaGift,
  FaSave,
  FaTimes
} from 'react-icons/fa';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    sound: true,
    desktop: true,
    email: false,
    types: {
      transaction: true,
      security: true,
      system: true,
      bbt: true,
      affiliate: true,
      promotion: false
    },
    priorities: {
      high: true,
      medium: true,
      low: false
    },
    quiet_hours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    }
  });

  const toggleSetting = (path) => {
    setSettings(prev => {
      const keys = path.split('.');
      const newSettings = { ...prev };
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = !current[keys[keys.length - 1]];
      return newSettings;
    });
  };

  const updateTime = (path, value) => {
    setSettings(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        [path]: value
      }
    }));
  };

  const saveSettings = () => {
    console.log('Saving notification settings:', settings);
    onClose();
  };

  const notificationTypes = [
    { key: 'transaction', label: 'Transactions', icon: FaExchangeAlt, color: 'text-green-400' },
    { key: 'security', label: 'Sécurité', icon: FaShieldAlt, color: 'text-red-400' },
    { key: 'system', label: 'Système', icon: FaCog, color: 'text-blue-400' },
    { key: 'bbt', label: 'BBT Token', icon: FaRocket, color: 'text-amber-400' },
    { key: 'affiliate', label: 'Affiliation', icon: FaUsers, color: 'text-purple-400' },
    { key: 'promotion', label: 'Promotions', icon: FaGift, color: 'text-pink-400' }
  ];

  const priorities = [
    { key: 'high', label: 'Haute priorité', color: 'text-red-400' },
    { key: 'medium', label: 'Priorité moyenne', color: 'text-yellow-400' },
    { key: 'low', label: 'Faible priorité', color: 'text-green-400' }
  ];

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 rounded-2xl border border-slate-600 max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FaBell className="text-blue-400" />
              Paramètres de Notifications
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-slate-600 rounded-lg transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Paramètres Généraux</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Notifications activées</div>
                    <div className="text-sm text-slate-400">Recevoir toutes les notifications</div>
                  </div>
                  <button
                    onClick={() => toggleSetting('enabled')}
                    className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {settings.enabled ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Son des notifications</div>
                    <div className="text-sm text-slate-400">Jouer un son lors de nouvelles notifications</div>
                  </div>
                  <button
                    onClick={() => toggleSetting('sound')}
                    className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {settings.sound ? <FaVolumeUp /> : <FaVolumeMute />}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Notifications bureau</div>
                    <div className="text-sm text-slate-400">Afficher les notifications sur le bureau</div>
                  </div>
                  <button
                    onClick={() => toggleSetting('desktop')}
                    className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {settings.desktop ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Notifications email</div>
                    <div className="text-sm text-slate-400">Recevoir les notifications importantes par email</div>
                  </div>
                  <button
                    onClick={() => toggleSetting('email')}
                    className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {settings.email ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Types de Notifications</h3>
              <div className="space-y-3">
                {notificationTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <div key={type.key} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${type.color}`} />
                        <span className="text-white font-medium">{type.label}</span>
                      </div>
                      <button
                        onClick={() => toggleSetting(`types.${type.key}`)}
                        className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {settings.types[type.key] ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Niveaux de Priorité</h3>
              <div className="space-y-3">
                {priorities.map((priority) => (
                  <div key={priority.key} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${priority.color.replace('text-', 'bg-')}`} />
                      <span className="text-white font-medium">{priority.label}</span>
                    </div>
                    <button
                      onClick={() => toggleSetting(`priorities.${priority.key}`)}
                      className="text-2xl text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {settings.priorities[priority.key] ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={saveSettings}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <FaSave className="w-4 h-4" />
            Enregistrer
          </button>
        </div>

        <style jsx>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #1e293b;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #475569;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
        `}</style>
      </motion.div>
    </motion.div>
  );
};

export default NotificationSettings;
